import {
  CONFIG_ACTOR,
  CONFIG_INSTANCE,
  METRIC_SPEC,
  type ConfigIdentity,
  type ConfigurationPort,
} from '../consumer/configuration';
import {
  parseConfigHistory,
  parseConfigReceipt,
  parseConfiguration,
  parseNetwork,
  parseObservationBasis,
  parseWarningConfiguration,
} from './configuration-wire';
import { createMonitoringRequest, MonitoringReadError } from './http';
export const CONFIG_ERROR_STATUS = {
  CONFIG_ID_CONFLICT: 409,
  CONFIG_REVISION_CONFLICT: 409,
  CONFIG_REQUEST_CONFLICT: 409,
  CONFIGURATION_NOT_FOUND: 404,
  INVALID_CONFIGURATION: 422,
  NO_STATE_CHANGE: 422,
  MALFORMED_REQUEST: 400,
  CONFIGURATION_CAPACITY: 429,
  STORAGE_FAILURE: 503,
} as const;
export type ConfigurationErrorCode = keyof typeof CONFIG_ERROR_STATUS | 'REQUEST_REJECTED';
export class ConfigurationRequestError extends MonitoringReadError {
  constructor(
    readonly status: number,
    readonly code: ConfigurationErrorCode,
  ) {
    super('HTTP');
  }
}
function parseFailure(status: number, value: unknown): MonitoringReadError {
  if (value === null || typeof value !== 'object' || Array.isArray(value))
    return new MonitoringReadError('WIRE');
  const data = value as Record<string, unknown>;
  if (
    Object.keys(data).length !== 2 ||
    !Object.hasOwn(data, 'error') ||
    !Object.hasOwn(data, 'message') ||
    typeof data.error !== 'string' ||
    typeof data.message !== 'string'
  )
    return new MonitoringReadError('WIRE');
  const code = data.error as ConfigurationErrorCode;
  if (
    code === 'REQUEST_REJECTED'
      ? [401, 403, 405, 413, 415].includes(status)
      : Object.hasOwn(CONFIG_ERROR_STATUS, code) && CONFIG_ERROR_STATUS[code] === status
  )
    return new ConfigurationRequestError(status, code);
  return new MonitoringReadError('WIRE');
}
export function configIdentity(
  expectedRevision: number,
  requestId = `nm-${crypto.randomUUID()}`,
): ConfigIdentity {
  return { requestId, expectedRevision, actor: CONFIG_ACTOR };
}
export function createConfigurationAdapter(options: {
  readonly baseOrigin: string;
  readonly fetchImpl?: typeof fetch;
}): ConfigurationPort {
  const request = createMonitoringRequest(options);
  const read = '/read-models/normal-monitoring';
  const write = '/normal-operations/configuration';
  function identity(body: ConfigIdentity) {
    if (
      !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(body.requestId) ||
      !Number.isSafeInteger(body.expectedRevision) ||
      body.expectedRevision < 1 ||
      body.actor !== CONFIG_ACTOR
    )
      throw new Error('Invalid configuration identity');
  }
  const objectId = (id: string) => {
    if (!/^[a-z][a-z0-9-]{0,63}$/.test(id))
      throw new Error('Invalid configuration object identity');
    return encodeURIComponent(id);
  };
  const display = (name: string, max = 128) => {
    if (
      !name.trim() ||
      name.length > max ||
      Array.from(name).some((char) => {
        const code = char.charCodeAt(0);
        return code < 32 || (code >= 127 && code <= 159);
      })
    )
      throw new Error('Invalid configuration text');
  };
  const mutate = async (path: string, id: string, body: ConfigIdentity, signal: AbortSignal) => {
    identity(body);
    const result = await request(path, parseConfigReceipt, signal, body, parseFailure);
    if (
      result.requestId !== body.requestId ||
      result.objectId !== id ||
      result.configRevision <= body.expectedRevision
    )
      throw new MonitoringReadError('WIRE');
    return result;
  };
  return {
    configuration: (signal) =>
      request(`${read}/configuration`, parseConfiguration, signal, undefined, parseFailure),
    network: (signal) => request(`${read}/network`, parseNetwork, signal, undefined, parseFailure),
    history: async (offset, limit, signal) => {
      if (
        !Number.isSafeInteger(offset) ||
        offset < 0 ||
        offset > 100000 ||
        !Number.isSafeInteger(limit) ||
        limit < 1 ||
        limit > 100
      )
        throw new Error('Invalid configuration history bounds');
      const result = await request(
        `${read}/configuration/history?offset=${offset}&limit=${limit}`,
        parseConfigHistory,
        signal,
        undefined,
        parseFailure,
      );
      if (result.offset !== offset || result.limit !== limit) throw new MonitoringReadError('WIRE');
      return result;
    },
    warningConfiguration: async (id, signal) => {
      const result = await request(
        `${read}/warnings/${encodeURIComponent(id)}/configuration`,
        parseWarningConfiguration,
        signal,
        undefined,
        parseFailure,
      );
      if (result.warningId !== id) throw new MonitoringReadError('WIRE');
      return result;
    },
    observationConfiguration: async (reference, signal) => {
      const result = await request(
        `${read}/observations/${encodeURIComponent(reference)}/configuration`,
        parseObservationBasis,
        signal,
        undefined,
        parseFailure,
      );
      if (result.reference !== reference) throw new MonitoringReadError('WIRE');
      return result;
    },
    createArea: (body, signal) => {
      objectId(body.areaId);
      display(body.displayName);
      if (body.instanceId !== CONFIG_INSTANCE) throw new Error('Invalid configuration instance');
      return mutate(`${write}/areas`, body.areaId, body, signal);
    },
    createSource: (body, signal) => {
      objectId(body.sourceId);
      objectId(body.areaId);
      display(body.displayName);
      const spec = METRIC_SPEC[body.metric];
      if (
        !spec ||
        body.unit !== spec.unit ||
        body.category !== spec.category ||
        body.instanceId !== CONFIG_INSTANCE
      )
        throw new Error('Invalid configuration metric combination');
      return mutate(`${write}/sources`, body.sourceId, body, signal);
    },
    renameArea: (id, body, signal) => {
      display(body.displayName);
      return mutate(`${write}/areas/${objectId(id)}/display-name`, id, body, signal);
    },
    renameSource: (id, body, signal) => {
      display(body.displayName);
      return mutate(`${write}/sources/${objectId(id)}/display-name`, id, body, signal);
    },
    setEnabled: (id, body, signal) => {
      display(body.reason, 256);
      if (typeof body.enabled !== 'boolean') throw new Error('Invalid enabled intent');
      return mutate(`${write}/sources/${objectId(id)}/enabled`, id, body, signal);
    },
  };
}
